import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  CContainer, 
  CButton, 
  CSpinner, 
  CBadge,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter
} from '@coreui/react';
import api from '../../api/axiosInstance';

import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';

// Helper Function: HTML entities clean aur KaTeX latex rendering
const RenderMathText = ({ rawContent }) => {
  if (!rawContent) return null;

  // 1. Convert HTML entities (&nbsp;, &gt;, etc.) and strip HTML tags
  let cleanText = rawContent
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/<[^>]*>?/gm, '');

  // 2. Split LaTeX blocks ($...$ and $$...$$)
  const parts = cleanText.split(/(\$\$[\s\S]*?\$\$|\$[^$]*\$)/g);

  return (
    <div style={{ 
      fontSize: '1.05rem', 
      lineHeight: '1.7', 
      color: '#1f2937', 
      wordBreak: 'break-word', 
      overflowWrap: 'anywhere' 
    }}>
      {parts.map((part, index) => {
        if (!part) return null;

        if (part.startsWith('$$') && part.endsWith('$$')) {
          const math = part.slice(2, -2).replace(/&nbsp;/gi, ' ');
          return (
            <div key={index} style={{ overflowX: 'auto', maxWidth: '100%', margin: '8px 0' }}>
              <BlockMath math={math} />
            </div>
          );
        } else if (part.startsWith('$') && part.endsWith('$')) {
          const math = part.slice(1, -1).replace(/&nbsp;/gi, ' ');
          return <InlineMath key={index} math={math} />;
        }
        return <span key={index}>{part}</span>;
      })}
    </div>
  );
};

const TopicQuestionsList = () => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const { section, title } = params;

  // Modal State
  const [modalVisible, setModalVisible] = useState(false);
  const [modalConfig, setModalConfig] = useState({ title: '', message: '', type: 'auth' });

  // Auth & Subscription Details
  const authToken = localStorage.getItem('authToken');
  const userTier = localStorage.getItem('subscriptionTier') || 'Alpha';
  const isSubActive = localStorage.getItem('isSubscriptionActive') === 'true';

  // Allowed Tiers Hierarchy for Solution Access
  const allowedTiers = ['Beeta', 'Gamma', 'Delta', 'Sigma'];
  const hasBeetaAccess = isSubActive && allowedTiers.includes(userTier);

  const getTopicFromUrl = () => {
    if (params.topicSlug) return params.topicSlug;
    if (params.topic) return params.topic;
    const pathParts = location.pathname.split('/').filter(Boolean);
    return pathParts[pathParts.length - 1] || '';
  };

  const topicNameRaw = getTopicFromUrl();
  const cleanTitle = title ? title.replace(/-/g, ' ') : '';
  const [chapterName, setChapterName] = useState('');
  const cleanTopicName = topicNameRaw ? topicNameRaw.replace(/-/g, ' ') : '';

  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (cleanTopicName) {
      fetchTopicQuestions();
    } else {
      setError("Invalid Topic Parameter.");
      setLoading(false);
    }
  }, [section, title, topicNameRaw]);

  const fetchTopicQuestions = async () => {
    try {
      setLoading(true);
      setError(null);

      const endpoint = `/api/content/GetTopicQuestions?section=${encodeURIComponent(section)}&category=${encodeURIComponent(cleanTitle)}&topic=${encodeURIComponent(cleanTopicName)}`;
      const res = await api.get(endpoint, { skipAuthCheck: true });

      if (res.data) {
        const fetchedQuestions = res.data.questions || [];
        setQuestions(fetchedQuestions);
        
        const extractedChapter = res.data.chapterName || fetchedQuestions[0]?.chapterName || '';
        setChapterName(extractedChapter);
      }
    } catch (err) {
      console.error("Error fetching topic questions:", err);
      setError("Failed to load questions for this topic.");
    } finally {
      setLoading(false);
    }
  };

  const handleGetSolution = (q) => {
    const rawQuestionText = q.customUrl || q.customeUrl || q.questionText || q.QuestionText || '';
    const truncatedText = getTruncatedText(rawQuestionText, 50);
    const slug = createCleanSlug(truncatedText);
    const targetUrl = `/content/view-question/${q.id}/${slug}`;

    // 1. LOGIN CHECK
    if (!authToken) {
      setModalConfig({
        title: 'Authentication Required 🔒',
        message: 'Login is necessary to view the solution.',
        type: 'auth'
      });
      setModalVisible(true);
      return;
    }

    // 2. BEETA SUBSCRIPTION CHECK
    if (!hasBeetaAccess) {
      setModalConfig({
        title: 'Beeta Subscription Required 👑',
        message: 'Ye solution unlock karne ke liye aapke paas Beeta ya usse upar ka active Subscription Plan hona chahiye.',
        type: 'upgrade'
      });
      setModalVisible(true);
      return;
    }

    // Access Granted
    navigate(targetUrl);
  };

  const handleModalConfirm = () => {
    setModalVisible(false);
    if (modalConfig.type === 'auth') {
      
       navigate('/login', { state: { from: `/${section}/${title}/topic/${topicNameRaw}` } });
    } else {
      navigate('/profile');
    }
  };

  const getTruncatedText = (htmlContent, maxLength = 50) => {
    if (!htmlContent) return '';
    const doc = new DOMParser().parseFromString(htmlContent, 'text/html');
    const plainText = doc.body.textContent || "";
    return plainText.trim().substring(0, maxLength);
  };

  const createCleanSlug = (rawUrl) => {
    if (!rawUrl || typeof rawUrl !== 'string') return '';
    return rawUrl
      .replace(/<[^>]*>/g, '')
      .replace(/%C2%A0/g, '-')
      .replace(/%20/g, '-')
      .replace(/[\/\\]/g, '-')
      .replace(/[^a-zA-Z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  if (loading) {
    return (
      <div className="text-center mt-5 py-5">
        <CSpinner color="primary" size="lg" />
        <p className="text-muted mt-2">Loading Questions & Formulas...</p>
      </div>
    );
  }

  if (error) {
    return (
      <CContainer className="text-center mt-5 py-4">
        <h5 className="text-danger mb-3">{error}</h5>
        <CButton color="primary" size="sm" onClick={() => navigate(-1)}>Go Back</CButton>
      </CContainer>
    );
  }

  return (
    <div style={{ overflowX: 'hidden', width: '100%' }}>
      
      {/* TOP HEADER BANNER (Full-Width Edge-to-Edge Blue) */}
      <div 
        className="text-white text-center py-5 shadow-sm mb-4" 
        style={{ 
          backgroundColor: '#0056b3',
          width: '100vw',
          position: 'relative',
          left: '50%',
          transform: 'translateX(-50%)'
        }}
      >
        <div className="container py-2 px-3">
          <CBadge 
            color="light"
            className="mb-3 px-3 py-2 fs-6 mx-auto rounded-pill shadow-xs text-primary fw-bold text-uppercase"
            style={{ letterSpacing: '0.5px' }}
          >
            {section?.toUpperCase()} • {cleanTitle.toUpperCase()}
          </CBadge>
          <h1 className="fw-bold mb-2 text-white display-6 text-capitalize">
            {chapterName ? `${chapterName} - ` : ''}{cleanTopicName}
          </h1>
          <p className="lead m-0 opacity-90 text-light fs-6 mb-3">
            Questions & Solutions Repository
          </p>
          <div>
            <CBadge color="danger" className="px-3 py-2 fs-6 rounded-pill fw-bold">
              {questions.length} Questions Available
            </CBadge>
          </div>
        </div>
      </div>

      {/* QUESTIONS LIST CONTAINER */}
      <CContainer className="pb-5" style={{ maxWidth: '1000px' }}>
        {questions.length > 0 ? (
          <div className="d-flex flex-column gap-3">
            {questions.map((q, index) => {
              const isLocked = !hasBeetaAccess;

              return (
                <div 
                  key={q.id || index}
                  className="bg-white p-3 p-md-4 rounded-3 border shadow-sm question-card"
                  style={{ transition: 'all 0.2s ease-in-out', cursor: 'pointer', overflow: 'hidden' }}
                  onClick={() => handleGetSolution(q)}
                >
                  <div className="d-flex flex-column flex-sm-row align-items-start justify-content-between gap-3">
                    
                    {/* Number Badge & Dynamic Lock */}
                    <div className="d-flex align-items-center gap-2" style={{ flexShrink: 0 }}>
                      <div 
                        className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center fw-bold shadow-sm"
                        style={{ width: '28px', height: '28px', fontSize: '0.85rem', flexShrink: 0 }}
                      >
                        {index + 1}
                      </div>
                      <span style={{ fontSize: '0.9rem', opacity: '0.8' }} title={isLocked ? "Beeta Required" : "Unlocked"}>
                        {isLocked ? "🔒" : "🔓"}
                      </span>
                    </div>

                    {/* Question Statement */}
                    <div className="flex-grow-1 w-100" style={{ minWidth: 0, overflowWrap: 'break-word' }}>
                      <RenderMathText rawContent={q.questionText} />
                    </div>

                    {/* Exam Tag */}
                    <div className="align-self-start text-sm-end" style={{ flexShrink: 0 }}>
                      <span 
                        className="fw-bold text-danger d-inline-block text-uppercase border border-danger-subtle bg-light rounded px-2 py-1"
                        style={{ fontSize: '0.75rem', whiteSpace: 'nowrap', letterSpacing: '0.3px' }}
                      >
                        {cleanTitle} {q.year || '2025'}
                      </span>
                    </div>

                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="alert alert-warning text-center">No questions available for this topic.</div>
        )}
      </CContainer>

      {/* COREUI MODAL FOR SUBSCRIPTION CHECK */}
      <CModal visible={modalVisible} onClose={() => setModalVisible(false)} alignment="center">
        <CModalHeader className="bg-dark text-white">
          <CModalTitle className="fw-bold fs-5">{modalConfig.title}</CModalTitle>
        </CModalHeader>
        <CModalBody className="py-4">
          <p className="fs-6 text-dark m-0">{modalConfig.message}</p>
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" onClick={() => setModalVisible(false)}>
            Close
          </CButton>
          <CButton color={modalConfig.type === 'upgrade' ? "success" : "primary"} className="text-white fw-bold" onClick={handleModalConfirm}>
            {modalConfig.type === 'upgrade' ? "Upgrade Plan Now" : "Login Now"}
          </CButton>
        </CModalFooter>
      </CModal>

      <style>{`
        .question-card:hover {
          border-color: #0A2540 !important;
          box-shadow: 0 6px 16px rgba(10, 37, 64, 0.1) !important;
          transform: translateY(-2px);
        }
        .katex {
          font-size: 1.05em;
        }
      `}</style>

    </div>
  );
};

export default TopicQuestionsList;